import { cn } from '@playerbase/ui';

// ---------------------------------------------------------------------------
// Single-select channel chip group. Sourced from the channels currently
// represented in the visible feedback rows - simpler than cross-reading
// discord_guild_channels for v1, and the chip set updates naturally as
// new channels accumulate feedback.
// ---------------------------------------------------------------------------

export interface ChannelChip {
  id: string;
  label: string;
}

interface FeedbackChannelFilterProps {
  channels: ChannelChip[];
  active: string | null;
  onChange: (channelId: string | null) => void;
}

export function FeedbackChannelFilter({
  channels,
  active,
  onChange,
}: FeedbackChannelFilterProps): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Filter by channel"
      className="flex flex-wrap gap-1 rounded-md border border-border bg-surface p-1"
    >
      <ChipButton
        label="All channels"
        active={active === null}
        onClick={() => onChange(null)}
      />
      {channels.map((c) => (
        <ChipButton
          key={c.id}
          label={c.label}
          active={active === c.id}
          onClick={() => onChange(c.id)}
        />
      ))}
    </div>
  );
}

interface ChipButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function ChipButton({ label, active, onClick }: ChipButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'rounded px-2.5 py-1 text-xs font-medium font-mono transition-colors',
        'hover:bg-surface-3',
        active
          ? 'bg-surface-3 text-text shadow-sm'
          : 'text-text-muted',
      )}
    >
      {label}
    </button>
  );
}
